/**
 * 엑셀/CSV 표 → 이용자(아동) 등록 행. 보호자 계정은 만들지 않는다(보호자 연결은 기존 초대 코드로만).
 * 표의 첫 줄은 머리글. 머리글 이름이 조금 달라도(공백·괄호) 알아보게 한다.
 */
import { validateGoalPeriod } from './clients.js';

export const CLIENT_IMPORT_MAX_ROWS = 200;

export const CLIENT_IMPORT_COLUMNS = ['이름', '출생연도', '회기단가', '담당 선생님', '목표', '목표 기간', '장애유형', '보호자 전화', '주소', '메모'] as const;
type Col = (typeof CLIENT_IMPORT_COLUMNS)[number];

const ALIASES: Record<string, Col> = {
  이름: '이름', 아동이름: '이름', 이용자: '이름', 이용자명: '이름', 성명: '이름',
  출생연도: '출생연도', 출생년도: '출생연도', 생년: '출생연도',
  회기단가: '회기단가', 단가: '회기단가', 회당단가: '회기단가',
  담당선생님: '담당 선생님', 담당치료사: '담당 선생님', 담당: '담당 선생님', 치료사: '담당 선생님', 선생님: '담당 선생님',
  목표: '목표', 치료목표: '목표',
  목표기간: '목표 기간', 치료기간: '목표 기간',
  장애유형: '장애유형', 진단: '장애유형',
  보호자전화: '보호자 전화', 보호자연락처: '보호자 전화', 연락처: '보호자 전화',
  주소: '주소', 메모: '메모', 비고: '메모',
};
const norm = (s: string) => s.replace(/[\s()（）·_\-*]/g, '').replace(/\(.*\)/, '').trim();

export interface ImportClientRow {
  /** 엑셀에서의 줄 번호(머리글이 1) */
  line: number;
  displayName: string;
  birthYear: number;
  unitPrice: string;
  therapistIds: string[];
  goals: Array<{ text: string; term: 'SHORT' | 'LONG' }>;
  goalPeriod?: { start: string; end: string };
  sensitive?: { disabilityType: string; guardianPhone: string; address: string; memo: string };
}
export interface ImportSheetResult { rows: ImportClientRow[]; errors: Array<{ line: number; message: string }> }

/** 표를 읽어 행으로 바꾼다. 선생님 이름은 이 센터 재직 선생님 id 로 바꾼다(없거나 동명이인이면 오류). */
export function readClientSheet(table: readonly (readonly string[])[], staff: ReadonlyArray<{ userId: string; name: string; active: boolean; role?: string }>): ImportSheetResult {
  const errors: ImportSheetResult['errors'] = [];
  const rows: ImportClientRow[] = [];
  const headerAt = table.findIndex((r) => r.some((c) => ALIASES[norm(c)] === '이름'));
  if (headerAt < 0) return { rows, errors: [{ line: 1, message: '머리글에 "이름" 칸이 없습니다. 양식 파일을 받아 쓰세요.' }] };
  const idx = new Map<Col, number>();
  table[headerAt]!.forEach((c, i) => { const k = ALIASES[norm(c)]; if (k && !idx.has(k)) idx.set(k, i); });
  if (!idx.has('출생연도')) errors.push({ line: headerAt + 1, message: '머리글에 "출생연도" 칸이 없습니다.' });
  const teachers = staff.filter((s) => s.active && (s.role === undefined || s.role === 'THERAPIST'));
  const body = table.slice(headerAt + 1);
  let count = 0;
  body.forEach((r, i) => {
    const line = headerAt + 2 + i;
    const get = (c: Col) => { const j = idx.get(c); return j === undefined ? '' : String(r[j] ?? '').trim(); };
    if (r.every((c) => !String(c ?? '').trim())) return;
    if (get('이름').startsWith('예시') || get('이름').startsWith('#')) return; // 양식의 안내 줄
    count++;
    if (count > CLIENT_IMPORT_MAX_ROWS) { if (count === CLIENT_IMPORT_MAX_ROWS + 1) errors.push({ line, message: `한 번에 ${CLIENT_IMPORT_MAX_ROWS}명까지 등록할 수 있습니다. 파일을 나눠 올리세요.` }); return; }
    const displayName = get('이름');
    if (!displayName) { errors.push({ line, message: '이름이 비어 있습니다.' }); return; }
    const by = get('출생연도').replace(/[년\s]/g, '');
    const birthYear = /^\d{4}$/.test(by) ? Number(by) : /^\d{4}[-./]/.test(by) ? Number(by.slice(0, 4)) : NaN;
    if (!Number.isInteger(birthYear)) { errors.push({ line, message: '출생연도는 2019처럼 네 자리로 적으세요.' }); return; }
    const therapistIds: string[] = [];
    let bad = false;
    for (const name of get('담당 선생님').split(/[,，/、]/).map((s) => s.trim()).filter(Boolean)) {
      const hit = teachers.filter((t) => t.name === name);
      if (hit.length !== 1) { errors.push({ line, message: hit.length ? `담당 선생님 "${name}"이(가) 두 명 이상입니다. 화면에서 직접 지정하세요.` : `담당 선생님 "${name}"을(를) 이 센터에서 찾을 수 없습니다.` }); bad = true; break; }
      therapistIds.push(hit[0]!.userId);
    }
    if (bad) return;
    const goals = get('목표').split(/\n|;|；/).map((s) => s.trim()).filter(Boolean).slice(0, 10).map((text) => ({ text: text.slice(0, 200), term: 'SHORT' as const }));
    let goalPeriod: ImportClientRow['goalPeriod'];
    const gp = get('목표 기간');
    if (gp) {
      const m = gp.match(/^(\d{4}[-./]\d{1,2}[-./]\d{1,2})\s*[~～–\-]\s*(\d{4}[-./]\d{1,2}[-./]\d{1,2})$/) ?? gp.match(/^(\S+)\s*~\s*(\S+)$/);
      const iso = (d: string) => { const [y, mo, da] = d.split(/[-./]/); return `${y}-${mo?.padStart(2, '0')}-${da?.padStart(2, '0')}`; };
      if (!m) { errors.push({ line, message: '목표 기간은 2026-03-01~2026-08-31처럼 적으세요.' }); return; }
      try { goalPeriod = validateGoalPeriod({ start: iso(m[1]!), end: iso(m[2]!) }); } catch (e) { errors.push({ line, message: e instanceof Error ? e.message : '목표 기간을 확인하세요.' }); return; }
    }
    const s = { disabilityType: get('장애유형'), guardianPhone: get('보호자 전화'), address: get('주소'), memo: get('메모') };
    rows.push({
      line, displayName, birthYear, unitPrice: get('회기단가').replace(/[원,\s]/g, '') || '0', therapistIds, goals,
      ...(goalPeriod ? { goalPeriod } : {}),
      ...(Object.values(s).some(Boolean) ? { sensitive: s } : {}),
    });
  });
  if (count === 0 && !errors.length) errors.push({ line: headerAt + 2, message: '등록할 줄이 없습니다.' });
  return { rows, errors };
}

/** 이름+출생연도가 같은 사람(이미 등록됐거나 파일 안에서 겹침). 막지는 않고 경고만 한다. */
export function duplicateKey(c: { displayName: string; birthYear: number }) { return `${c.displayName.replace(/\s/g, '')}|${c.birthYear}`; }

/** 양식 표(머리글 + 가상 예시 줄). CSV·XLSX 양식이 같은 내용을 쓴다. */
export function clientImportTemplateRows(): string[][] {
  return [
    [...CLIENT_IMPORT_COLUMNS],
    ['예시) 김가상', '2019', '55000', '담당 선생님 이름', '단어 두 개 이어 말하기', '2026-03-01~2026-08-31', '언어발달지연', '010-0000-0000', '서울시 예시구', '가상 예시 줄입니다(지우고 쓰세요)'],
  ];
}

/** 양식 파일(CSV). 예시 줄은 가상 인물이며 올릴 때 건너뛴다. */
export function clientImportTemplateCsv(): string {
  const cell = (c: string) => (/[",\r\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
  return '\uFEFF' + clientImportTemplateRows().map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}
